import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '../../styles/theme';
import {
  summarizeGoals,
  formatTextContent,
  getConfigurationItems,
  fetchUserInfoGoals,
  goalsMapToList,
} from '../../services/funnelService';

const PHASES = {
  SPEEDBUMPS: 'speedbumps',
  WARNING_SIGNS: 'warning_signs',
  WHERE_YOU_ARE: 'where_you_are',
  LOOKING_FORWARD: 'looking_forward',
};

const CHIP_RULES = [
  {
    match: /stir|trouble|drama/i,
    challenge: 'Stirring Trouble',
    desired: 'Steady Drive',
  },
  {
    match: /racing|bedtime|sleep|restorative|night/i,
    challenge: 'Racing Mind',
    desired: 'Restful Sleep',
  },
  {
    match: /eyelid|twitch/i,
    challenge: 'Twitchy Eyelid',
    desired: 'Physical Calm',
  },
  {
    match: /lying|unavailable|away from|office/i,
    challenge: 'Hiding Away',
    desired: 'Fully Present',
  },
  {
    match: /avoid(ing)?\s+(opportunity|opportunit)/i,
    challenge: 'Avoiding Opportunity',
    desired: 'Seizing Opportunity',
  },
  {
    match: /procrastinat|put\s+off|delay/i,
    challenge: 'Procrastination',
    desired: 'Decisive Action',
  },
  {
    match: /overwhelm|too\s+much/i,
    challenge: 'Overwhelm',
    desired: 'Clear Capacity',
  },
  {
    match: /irritab|short\s+temper|snap|anger/i,
    challenge: 'Irritability',
    desired: 'Emotional Control',
  },
  {
    match: /anxiety|worry|anxious/i,
    challenge: 'Anxiety',
    desired: 'Calm Focus',
  },
  {
    match: /exhaust|fatigue|tired|drain/i,
    challenge: 'Exhaustion',
    desired: 'Sustained Energy',
  },
  {
    match: /focus|distract|scatter/i,
    challenge: 'Scattered Focus',
    desired: 'Sharp Focus',
  },
  {
    match: /motivat|drive/i,
    challenge: 'Low Motivation',
    desired: 'Strong Drive',
  },
  {
    match: /burnout|burned\s+out/i,
    challenge: 'Burnout',
    desired: 'Mental Resilience',
  },
  {
    match: /isolat|withdraw|lonely/i,
    challenge: 'Isolation',
    desired: 'Connection',
  },
  {
    match: /numb|flat|empty/i,
    challenge: 'Feeling Flat',
    desired: 'Aliveness',
  },
  {
    match: /dread|avoid(ing)?\s+work|resistance/i,
    challenge: 'Dread',
    desired: 'Eager Momentum',
  },
];

const DEFAULT_CHALLENGE_CHIPS = ['Low Energy', 'Scattered Focus', 'Stress'];
const DEFAULT_DESIRED_CHIPS = ['Strong Mindstate', 'Clear Focus', 'Steady Momentum'];

const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'of',
  'to',
  'in',
  'on',
  'for',
  'with',
  'your',
  'you',
  'that',
  'this',
  'when',
  'why',
  'are',
  'is',
  'was',
  'were',
  'been',
  'being',
  'from',
  'into',
  'about',
  'just',
  'really',
  'still',
  'any',
  'these',
  'those',
]);

const titleCaseChip = (words) =>
  words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const shortenToChip = (label) => {
  const words = String(label || '')
    .replace(/[^a-zA-Z0-9\s'-]/g, ' ')
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => w && !STOP_WORDS.has(w.toLowerCase()));

  if (!words.length) return 'Challenge';
  return titleCaseChip(words.slice(0, 2));
};

const chipsFromSelections = (items) => {
  const challenges = [];
  const desired = [];
  const seenChallenge = new Set();
  const seenDesired = new Set();

  (items || []).forEach((item) => {
    const label = item?.label || '';
    const id = item?.id || '';
    const haystack = `${id} ${label}`;

    let challenge =
      item?.chip ||
      item?.challengeChip ||
      item?.shortLabel ||
      null;
    let desiredChip = item?.desiredChip || null;

    if (!challenge) {
      const rule = CHIP_RULES.find((r) => r.match.test(haystack));
      if (rule) {
        challenge = rule.challenge;
        desiredChip = desiredChip || rule.desired;
      } else {
        challenge = shortenToChip(label);
      }
    }

    if (!desiredChip) {
      const rule = CHIP_RULES.find((r) => r.match.test(haystack));
      desiredChip = rule?.desired || 'Clarity';
    }

    const challengeKey = challenge.toLowerCase();
    if (challenge && !seenChallenge.has(challengeKey)) {
      seenChallenge.add(challengeKey);
      challenges.push(challenge);
    }

    const desiredKey = desiredChip.toLowerCase();
    if (desiredChip && !seenDesired.has(desiredKey)) {
      seenDesired.add(desiredKey);
      desired.push(desiredChip);
    }
  });

  return {
    challengeChips: challenges.length ? challenges : DEFAULT_CHALLENGE_CHIPS,
    desiredChips: desired.length ? desired : DEFAULT_DESIRED_CHIPS,
  };
};

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px);
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
`;

const ProgressBarContainer = styled.div`
  width: 50vw;
  max-width: 280px;
  margin: 0 auto 32px auto;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => (props.progress / props.total) * 100}%;
  background: ${theme.colors.doryGreen};
  border-radius: 2px;
  transition: width ${theme.animation.durations.normal};
`;

const Title = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: 1.1;
  text-wrap: balance;
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0 0 ${theme.spacing.lg} 0;
  text-wrap: balance;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-bottom: ${theme.spacing.lg};
  text-align: left;
`;

const OptionCard = styled.button`
  width: 100%;
  text-align: left;
  border-radius: ${theme.borderRadius.lg};
  border: 2px solid ${(props) => (props.$selected ? theme.colors.doryGreen : 'rgba(64, 92, 115, 0.18)')};
  background: ${(props) => (props.$selected ? 'rgba(67, 95, 96, 0.12)' : '#fff')};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  display: flex;
  gap: ${theme.spacing.md};
  align-items: flex-start;
  transition: all ${theme.animation.durations.fast};

  &:hover {
    border-color: ${theme.colors.doryBlue};
    transform: translateY(-1px);
  }
`;

const Checkbox = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.$selected ? theme.colors.doryGreen : theme.colors.doryLighterBlue)};
  background: ${(props) => (props.$selected ? theme.colors.doryGreen : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  font-weight: ${theme.typography.weights.bold};
`;

const OptionLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.relaxed};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-top: ${theme.spacing.md};
`;

const PrimaryButton = styled.button`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.semiBold};
  min-height: 52px;
  width: 100%;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  color: #fff;
  cursor: pointer;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: #8b4a4a;
  margin: 0 0 ${theme.spacing.md} 0;
`;

const LoadingText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.doryBlue};
  font-style: italic;
  margin: ${theme.spacing.md} 0 ${theme.spacing.md} 0;
  width: 100%;
  text-align: left;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const pulseRing = keyframes`
  0%, 100% {
    opacity: 0.35;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const SummaryLoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${theme.spacing.xl} 0 ${theme.spacing.lg};
  text-align: center;
`;

const SummaryLoadingSubheading = styled.p`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.doryBlue};
  margin: 0 0 ${theme.spacing.lg} 0;
  text-wrap: balance;
`;

const LoadingIconWrap = styled.div`
  width: 112px;
  height: 112px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingIconRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid rgba(67, 95, 96, 0.18);
  border-top-color: ${theme.colors.doryGreen};
  animation: ${spin} 0.9s linear infinite;
`;

const LoadingIconCore = styled.svg`
  width: 56px;
  height: 56px;
  color: ${theme.colors.doryGreen};
  animation: ${pulseRing} 1.4s ease-in-out infinite;
`;

const CoachSummaryCard = styled.div`
  width: 100%;
  text-align: left;
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.md} 0 ${theme.spacing.sm} 0;
  background: linear-gradient(
    145deg,
    rgba(67, 95, 96, 0.12),
    rgba(64, 92, 115, 0.08)
  );
  border: none;
  box-shadow: none;
`;

const CoachSummaryText = styled.p`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0;
  text-wrap: wrap;
  overflow-wrap: break-word;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.47rem;
  width: 100%;
  margin: ${theme.spacing.sm} 0 ${theme.spacing.xl} 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: ${theme.typography.bodyFont};
  font-size: 0.93rem;
  font-weight: ${theme.typography.weights.regular};
  letter-spacing: ${theme.typography.letterSpacing.wide};
  line-height: 1.2;
  padding: 0.33rem 0.73rem;
  border-radius: ${theme.borderRadius.full};
  white-space: nowrap;
  border: none;
  background: ${(props) =>
    props.$tone === 'desired'
      ? 'rgba(67, 95, 96, 0.12)'
      : 'rgba(139, 74, 74, 0.08)'};
  color: ${(props) =>
    props.$tone === 'desired' ? theme.colors.doryGreen : '#8b4a4a'};
`;

const getPreviousTopGoals = (userProgress) =>
  goalsMapToList(userProgress?.userInfo?.goals);

const getProgress = (phase) => {
  switch (phase) {
    case PHASES.SPEEDBUMPS:
      return 1;
    case PHASES.WARNING_SIGNS:
      return 2;
    case PHASES.WHERE_YOU_ARE:
      return 3;
    case PHASES.LOOKING_FORWARD:
      return 4;
    default:
      return 1;
  }
};

const SpeedbumpsScreen = ({ data, onResponse, userProgress = {} }) => {
  const content = data?.content || {};
  const htmlIntro = content.text
    ? formatTextContent(content.text, userProgress)
    : null;

  const [topGoals, setTopGoals] = useState(() =>
    getPreviousTopGoals(userProgress),
  );

  const [phase, setPhase] = useState(PHASES.SPEEDBUMPS);
  const [speedbumpItems, setSpeedbumpItems] = useState([]);
  const [warningSignItems, setWarningSignItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(true);
  const [selectedSpeedbumpIds, setSelectedSpeedbumpIds] = useState([]);
  const [selectedWarningSignIds, setSelectedWarningSignIds] = useState([]);
  const [pendingSpeedbumps, setPendingSpeedbumps] = useState(null);
  const [pendingWarningSigns, setPendingWarningSigns] = useState(null);
  const [goalsSummary, setGoalsSummary] = useState('');
  const [lookingForwardSummary, setLookingForwardSummary] = useState('');
  const [challengeChips, setChallengeChips] = useState(DEFAULT_CHALLENGE_CHIPS);
  const [desiredChips, setDesiredChips] = useState(DEFAULT_DESIRED_CHIPS);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [phase]);

  useEffect(() => {
    let cancelled = false;
    const shortcode = userProgress?.shortcode;

    const loadGoalsFromUserDoc = async () => {
      if (!shortcode) {
        setTopGoals(getPreviousTopGoals(userProgress));
        return;
      }

      const fromDoc = await fetchUserInfoGoals(shortcode);
      if (cancelled) return;

      if (fromDoc.length) {
        setTopGoals(fromDoc);
      } else {
        setTopGoals(getPreviousTopGoals(userProgress));
      }
    };

    loadGoalsFromUserDoc();
    return () => {
      cancelled = true;
    };
    // Re-read users/{shortcode}.userInfo.goals whenever session shortcode is available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProgress?.shortcode]);

  useEffect(() => {
    let cancelled = false;

    const loadItems = async () => {
      setIsLoadingItems(true);
      setError('');
      try {
        const [speedbumps, warningSigns] = await Promise.all([
          getConfigurationItems('speedbumps'),
          getConfigurationItems('warningsigns'),
        ]);
        if (cancelled) return;
        setSpeedbumpItems(speedbumps);
        setWarningSignItems(warningSigns);
      } catch (err) {
        console.error('Failed to load speedbumps configuration:', err);
        if (!cancelled) {
          setError('Failed to load options. Please refresh and try again.');
        }
      } finally {
        if (!cancelled) setIsLoadingItems(false);
      }
    };

    loadItems();
    return () => {
      cancelled = true;
    };
  }, []);

  const itemsFromIds = (ids, catalog) =>
    ids
      .map((id) => catalog.find((item) => item.id === id))
      .filter(Boolean)
      .map((item) => ({
        id: item.id,
        label: item.label,
        ...(item.chip ? { chip: item.chip } : {}),
        ...(item.desiredChip ? { desiredChip: item.desiredChip } : {}),
      }));

  const toggleId = (setter) => (id) => {
    setter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const goToSummary = async (speedbumps, warningSigns) => {
    setPendingSpeedbumps(speedbumps);
    setPendingWarningSigns(warningSigns);
    setGoalsSummary('');
    setLookingForwardSummary('');
    setError('');

    const localChips = chipsFromSelections([
      ...(speedbumps || []),
      ...(warningSigns || []),
    ]);
    setChallengeChips(localChips.challengeChips);
    setDesiredChips(localChips.desiredChips);

    setPhase(PHASES.WHERE_YOU_ARE);
    setIsSummarizing(true);

    const fallbackWhereYouAre = warningSigns?.length
      ? topGoals.length
        ? 'With clear goals in front of you, the patterns you checked are already slowing you down — and left unaddressed, things will continue to trend in the wrong direction.'
        : 'The patterns you checked are already slowing progress toward your goals — and left unaddressed, things will continue to trend in the wrong direction.'
      : topGoals.length
        ? 'You have clear goals in front of you, and the patterns you checked show where burnout has been getting in the way.'
        : 'The patterns you checked show where burnout has been getting in the way of the progress you want.';
    const primaryGoal = topGoals[0] || '';
    const fallbackLookingForward = primaryGoal
      ? `Dory is proven to rebuild the strength of your mindstate so the speedbumps fade — unlocking momentum, clarity, and a lighter, more exciting path forward. Choose to make a change today and you will feel more in control and driven as you move toward ${primaryGoal}.`
      : 'Dory is proven to rebuild the strength of your mindstate so these speedbumps fade — unlocking momentum, clarity, and a lighter path toward your goals. Choose to make a change today and you will feel more in control, more driven, and genuinely hopeful about what comes next.';

    try {
      const result = await summarizeGoals({
        topGoals,
        speedbumps,
        warningSigns,
      });
      setGoalsSummary(result?.whereYouAre || fallbackWhereYouAre);
      setLookingForwardSummary(
        result?.lookingForward || fallbackLookingForward,
      );

      const geminiChallenges = Array.isArray(result?.challengeChips)
        ? result.challengeChips
            .map((chip) => String(chip || '').trim())
            .filter(Boolean)
        : [];
      const geminiDesired = Array.isArray(result?.desiredChips)
        ? result.desiredChips
            .map((chip) => String(chip || '').trim())
            .filter(Boolean)
        : [];

      if (geminiChallenges.length >= 2) {
        setChallengeChips(geminiChallenges.slice(0, 6));
      }
      if (geminiDesired.length >= 2) {
        setDesiredChips(geminiDesired.slice(0, 6));
      }
    } catch (err) {
      console.error('Speedbumps summary failed:', err);
      setGoalsSummary(fallbackWhereYouAre);
      setLookingForwardSummary(fallbackLookingForward);
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleSpeedbumpsContinue = () => {
    setError('');
    setPhase(PHASES.WARNING_SIGNS);
  };

  const handleWarningSignsContinue = () => {
    goToSummary(
      itemsFromIds(selectedSpeedbumpIds, speedbumpItems),
      itemsFromIds(selectedWarningSignIds, warningSignItems),
    );
  };

  const finishAndSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    const speedbumps =
      pendingSpeedbumps || itemsFromIds(selectedSpeedbumpIds, speedbumpItems);
    const warningSigns =
      pendingWarningSigns ||
      itemsFromIds(selectedWarningSignIds, warningSignItems);

    const responseData = {
      checkpointKey: data.key,
      type: 'speedbumps',
      speedbumps,
      warningSigns,
      whereYouAre: goalsSummary || '',
      lookingForward: lookingForwardSummary || '',
      challengeChips,
      desiredChips,
      summary: [goalsSummary, lookingForwardSummary].filter(Boolean).join(' '),
      timestamp: Date.now(),
    };

    try {
      await onResponse?.(responseData);
    } catch (err) {
      console.error('Failed to save speedbumps response:', err);
      setError('Failed to save your responses. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderOptionList = (catalog, selectedIds, onToggle) => (
    <OptionList>
      {catalog.map((item) => {
        const selected = selectedIds.includes(item.id);
        return (
          <OptionCard
            key={item.id}
            type="button"
            $selected={selected}
            onClick={() => onToggle(item.id)}
            aria-pressed={selected}
          >
            <Checkbox $selected={selected}>{selected ? '✓' : ''}</Checkbox>
            <OptionLabel>{item.label}</OptionLabel>
          </OptionCard>
        );
      })}
    </OptionList>
  );

  const renderPhase = () => {
    if (isLoadingItems) {
      return <LoadingText>Loading options…</LoadingText>;
    }

    if (phase === PHASES.SPEEDBUMPS) {
      return (
        <>
          {htmlIntro && (
            <Description dangerouslySetInnerHTML={{ __html: htmlIntro }} />
          )}
          <Title>
            Which of these speedbumps are slowing your progress toward your{' '}
            {topGoals.length === 1 ? 'goal' : 'goals'}?
          </Title>
          <Description>Select any that are relevant to you.</Description>
          {speedbumpItems.length === 0 ? (
            <ErrorText>No speedbumps are configured yet.</ErrorText>
          ) : (
            renderOptionList(
              speedbumpItems,
              selectedSpeedbumpIds,
              toggleId(setSelectedSpeedbumpIds),
            )
          )}
          {error && <ErrorText>{error}</ErrorText>}
          <ButtonGroup>
            <PrimaryButton
              onClick={handleSpeedbumpsContinue}
              disabled={speedbumpItems.length === 0}
            >
              Continue
            </PrimaryButton>
          </ButtonGroup>
        </>
      );
    }

    if (phase === PHASES.WARNING_SIGNS) {
      return (
        <>
          <Title>
            Have you observed any of these signals that indicate you're still trending toward burnout?
          </Title>
          <Description>Select any that are relevant to you.</Description>
          {warningSignItems.length === 0 ? (
            <ErrorText>No warning signs are configured yet.</ErrorText>
          ) : (
            renderOptionList(
              warningSignItems,
              selectedWarningSignIds,
              toggleId(setSelectedWarningSignIds),
            )
          )}
          {error && <ErrorText>{error}</ErrorText>}
          <ButtonGroup>
            <PrimaryButton
              onClick={handleWarningSignsContinue}
              disabled={isSummarizing || warningSignItems.length === 0}
            >
              Continue
            </PrimaryButton>
          </ButtonGroup>
        </>
      );
    }

    if (phase === PHASES.WHERE_YOU_ARE) {
      const showSummary = !isSummarizing && !!goalsSummary;

      return (
        <>
          {isSummarizing && (
            <SummaryLoadingState aria-live="polite" aria-busy="true">
              <SummaryLoadingSubheading>
                Understanding your current path. Searching for a stronger path forward.
              </SummaryLoadingSubheading>
              <LoadingIconWrap aria-hidden="true">
                <LoadingIconRing />
                <LoadingIconCore
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="6.5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                  />
                  <path
                    d="M16.5 16.5L21 21"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                  <path
                    d="M11 8.25V13.75"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                  <path
                    d="M8.25 11H13.75"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </LoadingIconCore>
              </LoadingIconWrap>
            </SummaryLoadingState>
          )}

          {showSummary && (
            <>
              <Title>Here&apos;s the path you're on right now.</Title>
              <CoachSummaryCard>
                <CoachSummaryText>{goalsSummary}</CoachSummaryText>
              </CoachSummaryCard>
              <ChipRow aria-label="Key challenges">
                {challengeChips.map((chip) => (
                  <Chip key={chip} $tone="challenge">
                    {chip}
                  </Chip>
                ))}
              </ChipRow>
              {error && <ErrorText>{error}</ErrorText>}
              <ButtonGroup>
                <PrimaryButton
                  onClick={() => setPhase(PHASES.LOOKING_FORWARD)}
                >
                  Continue
                </PrimaryButton>
              </ButtonGroup>
            </>
          )}

          {error && !showSummary && <ErrorText>{error}</ErrorText>}
        </>
      );
    }

    if (phase === PHASES.LOOKING_FORWARD) {
      return (
        <>
          <Title>Here&apos;s where Dory will take you.</Title>

          {lookingForwardSummary && (
            <CoachSummaryCard>
              <CoachSummaryText>{lookingForwardSummary}</CoachSummaryText>
            </CoachSummaryCard>
          )}

          <ChipRow aria-label="Desired states with Dory">
            {desiredChips.map((chip) => (
              <Chip key={chip} $tone="desired">
                {chip}
              </Chip>
            ))}
          </ChipRow>

          {error && <ErrorText>{error}</ErrorText>}
          <ButtonGroup>
            <PrimaryButton
              onClick={finishAndSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : "I'm Ready"}
            </PrimaryButton>
          </ButtonGroup>
        </>
      );
    }

    return null;
  };

  return (
    <Container ref={containerRef}>
      <Content>
        <ProgressBarContainer>
          <ProgressFill progress={getProgress(phase)} total={4} />
        </ProgressBarContainer>
        {renderPhase()}
      </Content>
    </Container>
  );
};

export default SpeedbumpsScreen;
